import React, { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RotateCcw, Download } from "lucide-react";
import { fmtDate } from "@/lib/prime";
import { errorReport } from "./PrimeImportPreview";
import { toast } from "sonner";

const STATUS = { applied: "Applied", undone: "Undone", partially_undone: "Partly undone (conflicts)", applying: "Applying" };

export function PrimeImportHistory({ org, reloadKey }) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => { api.get("/prime/import/batches", { params: { organization_id: org } }).then((r) => setItems(r.data.items)).catch((e) => toast.error(apiError(e))); }, [org]);
  useEffect(() => { load(); }, [load, reloadKey]);
  const undo = async (b) => {
    if (!window.confirm(`Undo "${b.filename}"? Values changed by later edits or imports are left alone and flagged.`)) return;
    try { const { data } = await api.post(`/prime/import/${b.id}/undo`); toast.success(`${data.restored} restored${data.conflicts.length ? `, ${data.conflicts.length} conflicts flagged` : ""}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const report = async (b) => {
    const { data } = await api.get(`/prime/import/${b.id}`);
    const url = URL.createObjectURL(new Blob([errorReport(data.rows, data.excluded)], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `prime-import-errors-${b.id.slice(0, 8)}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  if (!items) return <p className="text-sm text-slate-400">Loading…</p>;
  if (!items.length) return <p className="text-sm text-slate-500" data-testid="prime-history-empty">No Prime imports have been applied for this group yet.</p>;
  return (
    <ul className="space-y-2" data-testid="prime-history">
      {items.map((b) => (
        <li key={b.id} className="rounded-lg border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-3" data-testid={`prime-batch-${b.id}`}>
          <div className="flex-1 min-w-[220px]">
            <p className="text-sm font-medium text-slate-900">{b.filename} <span className="text-xs text-slate-500">· {b.periods?.join(", ")}</span></p>
            <p className="text-xs text-slate-500">{b.created_by_name} · {fmtDate(b.applied_at || b.created_at)} · {b.counts?.applied ?? 0} updated, {b.counts?.excluded ?? 0} excluded · {STATUS[b.status] || b.status}</p>
            {(b.undo_conflicts || []).map((x) => <p key={x.row} className="text-xs text-amber-700">Row {x.row} ({x.period}): {x.reason}</p>)}
          </div>
          <Button size="sm" variant="outline" onClick={() => report(b)} data-testid={`prime-batch-report-${b.id}`}><Download className="w-4 h-4 mr-1" />Error report</Button>
          {b.status === "applied" && <Button size="sm" variant="outline" onClick={() => undo(b)} data-testid={`prime-undo-${b.id}`}><RotateCcw className="w-4 h-4 mr-1" />Undo Import</Button>}
        </li>))}
    </ul>
  );
}
